import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, HardHat } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import InductionContent from '../../components/induction/InductionContent';
import inductionService from '../../services/induction';
import type { EquipmentInductionDetail, MyEquipmentRecord } from '../../types/induction';
import { formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';

function errorCode(err: unknown): string | undefined {
  return (err as { code?: string } | undefined)?.code;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * An equipment induction's safety requirements, for the person reading their
 * own. Anyone in the org can read one; only someone a supervisor has signed
 * off on it is asked to acknowledge it.
 */
export default function ReadEquipmentInduction() {
  const { id } = useParams<{ id: string }>();
  const inductionId = Number(id);
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const queryClient = useQueryClient();

  const [induction, setInduction] = useState<EquipmentInductionDetail | null>(null);
  const [record, setRecord] = useState<MyEquipmentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, mine] = await Promise.all([inductionService.get(inductionId), inductionService.mine()]);
      setInduction(detail);
      setRecord(mine.equipment.find((r) => r.induction_id === inductionId) ?? null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load this induction.'));
    } finally {
      setLoading(false);
    }
  }, [inductionId]);

  useEffect(() => {
    setAgreed(false);
    setNotice(null);
    void load();
  }, [load]);

  const handleAcknowledge = async () => {
    if (!induction || !agreed) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await inductionService.acknowledge('equipment_induction', induction.id, induction.content_version);
      void queryClient.invalidateQueries({ queryKey: ['inductions', 'mine'] });
      await load();
    } catch (err) {
      if (errorCode(err) === 'STALE_VERSION') {
        setAgreed(false);
        setNotice('These safety requirements were updated while you were reading. The new version is shown — please read it again before acknowledging.');
        await load();
      } else {
        setError(errorMessage(err, 'Could not record your acknowledgement. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={induction?.title || 'Equipment induction'}
        subtitle="Safety requirements"
        icon={<HardHat className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {notice && (
          <div className="bg-warning-50 border border-warning-200 rounded-lg px-4 py-3 text-sm text-warning-800 break-words">{notice}</div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
        )}

        {loading && !induction ? (
          <div className="h-64 bg-neutral-100 rounded-xl animate-pulse" />
        ) : induction && (
          <>
            <article className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 min-w-0 space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-neutral-900 break-words">{induction.title}</h2>
                {induction.equipment_name && (
                  <p className="text-sm text-neutral-500 break-words">{induction.equipment_name}</p>
                )}
              </div>
              <InductionContent
                induction={induction}
                showPracticalChecks
                requiredCertifications={induction.required_certifications}
              />
            </article>

            <section className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 sm:p-5 space-y-3">
              {!record ? (
                <p className="text-sm text-neutral-700 break-words">
                  You are not signed off on this machine yet. Ask your supervisor for an induction before using it.
                </p>
              ) : (
                <>
                  <p className="text-sm text-neutral-700 break-words">
                    Signed off{record.assessed_by_name ? ` by ${record.assessed_by_name}` : ''} on{' '}
                    {formatDateTimeInTz(record.assessed_at, orgTz)}
                    {record.expires_on && (
                      <> · {record.status === 'expired' ? 'expired' : 'expires'} {formatLocalDateInTz(record.expires_on, orgTz)}</>
                    )}
                  </p>
                  {record.acknowledged_at ? (
                    <p className="flex items-start gap-2 text-sm font-medium text-success-700 break-words">
                      <CheckCircle2 className="h-5 w-5 shrink-0" />
                      <span className="min-w-0">
                        Safety requirements acknowledged {formatDateTimeInTz(record.acknowledged_at, orgTz)}
                      </span>
                    </p>
                  ) : (
                    <>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-neutral-700">
                          I have read and understood these safety requirements
                        </span>
                      </label>
                      <p className="text-xs text-neutral-500">The date and time you acknowledge them are recorded.</p>
                    </>
                  )}
                </>
              )}
              <div className="flex flex-wrap gap-2">
                {record && !record.acknowledged_at && (
                  <Button onClick={handleAcknowledge} disabled={!agreed || submitting} isLoading={submitting}>
                    Acknowledge
                  </Button>
                )}
                <Button variant="outline" onClick={() => navigate('/my-inductions')} disabled={submitting}>
                  Back to my inductions
                </Button>
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
}
